import React, { type FunctionComponent, useCallback, useMemo, useState, useEffect } from 'react'
import ReactSelect, { type Styles } from 'react-select'
import { colors } from '@retailer-platform/shared-components'
import { useRetailerId } from 'utils/routing/params.hooks'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import { getDropdownStyles } from 'domains/aggregate-store-performance/utils/helpers/Dropdown.utils'
import { trackFiltersChanged } from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.tracking'
import { Menu } from './components/Menu'
import { Option } from './components/Option'
import { Placeholder } from './components/PlaceHolder'
import {
  type FiltersDropdownOption,
  type GroupedFiltersDropdownOptions,
} from './utils/FiltersDropdown.types'

const selectStyles: Styles = getDropdownStyles({
  control: state => ({
    width: state.hasValue ? '104px' : '84px',
    borderColor:
      state.isSelected || state.isFocused || state.hasValue
        ? colors.SECONDARY.DARK
        : colors.GRAYSCALE.X10,
    transition: 'width 0s',
  }),
  dropdownIndicator: () => ({ display: 'none' }),
})

interface FiltersDropdownProps {
  filters: GroupedFiltersDropdownOptions[]
  selectedFilters: FiltersDropdownOption[]
  onSelectionChange: (values: FiltersDropdownOption[]) => void
  isLoading: boolean
  showMenu?: boolean
  onMenuClose?: () => void
}

export const FiltersDropdown: FunctionComponent<FiltersDropdownProps> = ({
  filters = [],
  selectedFilters = [],
  onSelectionChange,
  isLoading,
  showMenu = false,
  onMenuClose,
}) => {
  const retailerId = useRetailerId()

  const messages = useDomainMessages({
    label: 'aggregateStorePerformanceDomain.filtersDropdown.label',
  })

  const [selection, setSelection] = useState([])
  const [selectionOnOpen, setSelectionOnOpen] = useState(selection)
  const [isMenuOpen, setIsMenuOpen] = useState(showMenu)

  useEffect(() => {
    setIsMenuOpen(showMenu)
  }, [showMenu])

  useEffect(() => {
    setSelection(selectedFilters)
  }, [selectedFilters])

  const [controlMessage, setControlMessage] = useState(messages.label)

  const options: GroupedFiltersDropdownOptions[] = useMemo(() => [...filters], [filters])

  useEffect(() => {
    const message =
      selection.length > 0 ? `${messages.label} (${selection.length})` : messages.label
    setControlMessage(message)
  }, [messages.label, selection])

  const handleChange = useCallback((values: FiltersDropdownOption[]) => {
    setSelection(values)
  }, [])

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
    onMenuClose?.()
  }, [onMenuClose])

  const handleSave = useCallback(() => {
    onSelectionChange(selection)
    setSelectionOnOpen(selection)
    trackFiltersChanged({ retailerId, selectedFilters: selection })
    closeMenu()
  }, [closeMenu, onSelectionChange, retailerId, selection])

  const handleClear = useCallback(() => {
    setSelection([])
  }, [])

  const handleBlur = useCallback(() => {
    setSelection(selectionOnOpen)
    closeMenu()
  }, [closeMenu, selectionOnOpen])

  const handleOnOpenMenu = useCallback(() => {
    setIsMenuOpen(true)
    setSelectionOnOpen(selection)
  }, [selection])

  return (
    <ReactSelect
      components={{ Menu, Option, Placeholder }}
      controlShouldRenderValue={false}
      isMulti
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isSearchable={false}
      isClearable={false}
      isLoading={isLoading}
      name="filtersDropdown"
      ariaLabel={messages.label}
      placeholder={controlMessage}
      value={selection}
      options={options}
      styles={selectStyles}
      menuIsOpen={isMenuOpen}
      onSave={handleSave}
      onClear={handleClear}
      onChange={handleChange}
      onMenuOpen={handleOnOpenMenu}
      onBlur={handleBlur}
    />
  )
}
